import React, { useRef, useEffect, useMemo, useCallback, useState, ChangeEvent } from 'react';
import {
  Send,
  Square,
  Loader2,
  Sparkles,
  Paperclip,
  Command,
  CheckCircle,
  AlertCircle,
  FileText,
  XCircle,
} from 'lucide-react';
import type { ChatStatus } from '../../types/chat';
import type { FileInfo } from '../../hooks/useFileList';
import type { UploadState } from '../../hooks/useFileUpload';
import { FileAutocomplete } from './FileAutocomplete';
import { VoiceButton } from '../voice/VoiceButton';

const MAX_CHARACTERS = 4000;

interface PromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (text: string) => void;
  onStop?: () => void;
  status: ChatStatus;
  placeholder?: string;
  onFilesSelected?: (files: FileList) => void;
  fileUploadEnabled?: boolean;
  /** Workspace files for @-mention autocomplete */
  workspaceFiles?: FileInfo[];
  uploads?: UploadState[];
  filesSidebarOpen?: boolean;
  /** Session ID for voice mode */
  sessionId?: string;
  /** User ID for voice mode */
  userId?: string;
  /** Spec 052 Step 9: lazy session bootstrap for voice-first flow. */
  onCreateSession?: () => Promise<string | null>;
}

interface SlashCommand {
  label: string;
  description: string;
  value: string;
}

export const PromptInput = React.memo(function PromptInput({
  value,
  onChange,
  onSubmit,
  onStop,
  status,
  placeholder = 'Ask me anything...',
  onFilesSelected,
  fileUploadEnabled = false,
  workspaceFiles = [],
  uploads = [],
  filesSidebarOpen = true,
  sessionId,
  userId,
  onCreateSession,
}: PromptInputProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Autocomplete state
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [autocompleteIndex, setAutocompleteIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashIndex, setSlashIndex] = useState(0);

  const isDisabled = status === 'streaming' || status === 'submitted';
  const isStreaming = status === 'streaming';
  const isSubmitted = status === 'submitted';

  const characterCount = value.length;
  const isNearLimit = characterCount > MAX_CHARACTERS * 0.9;
  const isOverLimit = characterCount > MAX_CHARACTERS;

  const slashCommands = useMemo<SlashCommand[]>(
    () => [
      {
        label: 'Plan',
        description: 'Ask the assistant to plan before answering',
        value: '/plan',
      },
      {
        label: 'Summarize',
        description: 'Summarize the current conversation',
        value: '/summarize',
      },
      {
        label: 'Files',
        description: 'List relevant workspace files',
        value: '/files',
      },
      {
        label: 'Search',
        description: 'Search the web for sources',
        value: '/search',
      },
      {
        label: 'Tasks',
        description: 'Show the current plan status',
        value: '/tasks',
      },
    ],
    []
  );

  const characterCountColor = useMemo(() => {
    if (isOverLimit) return 'text-red-500';
    if (isNearLimit) return 'text-amber-500';
    return 'text-gray-400';
  }, [isNearLimit, isOverLimit]);

  useEffect(() => {
    if (status === 'ready') {
      inputRef.current?.focus();
    }
  }, [status]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [value]);

  // Filter files for autocomplete based on query
  const filteredFiles = useMemo(() => {
    if (!autocompleteOpen) return [];
    return workspaceFiles.filter((file) =>
      file.name.toLowerCase().includes(autocompleteQuery.toLowerCase())
    );
  }, [workspaceFiles, autocompleteQuery, autocompleteOpen]);

  const filteredCommands = useMemo(() => {
    if (!slashOpen) return [];
    const query = slashQuery.toLowerCase();
    return slashCommands.filter((command) =>
      command.value.toLowerCase().includes(query)
    );
  }, [slashOpen, slashQuery, slashCommands]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setAutocompleteIndex(0);
  }, [filteredFiles.length]);

  useEffect(() => {
    setSlashIndex(0);
  }, [filteredCommands.length]);

  // Close autocomplete
  const closeAutocomplete = useCallback(() => {
    setAutocompleteOpen(false);
    setAutocompleteQuery('');
    setAutocompleteIndex(0);
    setMentionStart(null);
  }, []);

  const closeSlashMenu = useCallback(() => {
    setSlashOpen(false);
    setSlashQuery('');
    setSlashIndex(0);
  }, []);

  // Handle file selection from autocomplete
  const handleFileSelect = useCallback(
    (file: FileInfo) => {
      if (mentionStart === null) return;

      // Replace @query with @filename
      const beforeMention = value.slice(0, mentionStart);
      const afterMention = value.slice(mentionStart + 1 + autocompleteQuery.length);
      const newValue = `${beforeMention}@${file.name}${afterMention}`;

      onChange(newValue);
      closeAutocomplete();

      // Focus back on input and set cursor position after the inserted filename
      setTimeout(() => {
        const textarea = inputRef.current;
        if (textarea) {
          textarea.focus();
          const cursorPos = mentionStart + 1 + file.name.length;
          textarea.setSelectionRange(cursorPos, cursorPos);
        }
      }, 0);
    },
    [value, mentionStart, autocompleteQuery, onChange, closeAutocomplete]
  );

  const handleSlashSelect = useCallback(
    (command: SlashCommand) => {
      const newValue = `${command.value} `;
      onChange(newValue);
      closeSlashMenu();

      setTimeout(() => {
        const textarea = inputRef.current;
        if (textarea) {
          textarea.focus();
          const cursorPos = newValue.length;
          textarea.setSelectionRange(cursorPos, cursorPos);
        }
      }, 0);
    },
    [onChange, closeSlashMenu]
  );

  // Handle input change with @-mention detection
  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart || 0;
      onChange(newValue);

      // Check for @ trigger
      // Find the last @ before cursor that's at start or after a space
      let foundMentionStart: number | null = null;
      for (let i = cursorPos - 1; i >= 0; i--) {
        const char = newValue[i];
        if (char === ' ' || char === '\n') {
          // Hit a space/newline before finding @, no active mention
          break;
        }
        if (char === '@') {
          // Check if @ is at start or after space/newline
          if (i === 0 || newValue[i - 1] === ' ' || newValue[i - 1] === '\n') {
            foundMentionStart = i;
          }
          break;
        }
      }

      if (foundMentionStart !== null) {
        const query = newValue.slice(foundMentionStart + 1, cursorPos);
        setMentionStart(foundMentionStart);
        setAutocompleteQuery(query);
        setAutocompleteOpen(true);
        closeSlashMenu();
      } else {
        closeAutocomplete();
        if (newValue.startsWith('/')) {
          const firstSpace = newValue.indexOf(' ');
          if (firstSpace === -1 || cursorPos <= firstSpace) {
            setSlashOpen(true);
            setSlashQuery(newValue.slice(1, cursorPos));
          } else {
            closeSlashMenu();
          }
        } else {
          closeSlashMenu();
        }
      }
    },
    [onChange, closeAutocomplete, closeSlashMenu]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !isDisabled && !isOverLimit) {
      onSubmit(value.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle autocomplete navigation
    if (autocompleteOpen && filteredFiles.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setAutocompleteIndex((prev) =>
          prev < filteredFiles.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setAutocompleteIndex((prev) =>
          prev > 0 ? prev - 1 : filteredFiles.length - 1
        );
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleFileSelect(filteredFiles[autocompleteIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAutocomplete();
        return;
      }
    }

    if (slashOpen && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((prev) =>
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex((prev) =>
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        );
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleSlashSelect(filteredCommands[slashIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSlashMenu();
        return;
      }
    }

    // Normal submit on Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleStopClick = () => {
    onStop?.();
  };

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0 && onFilesSelected) {
        onFilesSelected(files);
      }
      // Reset input
      e.target.value = '';
    },
    [onFilesSelected]
  );

  const uploadsToShow = useMemo(() => {
    if (!uploads.length) return [];
    return uploads.slice(-4);
  }, [uploads]);

  return (
    <form
      onSubmit={handleSubmit}
      className="sticky bottom-0 z-10 mx-auto w-full max-w-4xl bg-gradient-to-t from-white via-white to-white/95 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900/95 px-2 pt-3 md:px-4 safe-bottom-pad"
    >
      {/* Hidden file input */}
      {fileUploadEnabled && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          data-testid="file-input"
        />
      )}

      {fileUploadEnabled && uploadsToShow.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {uploadsToShow.map((upload) => {
            const isUploading =
              upload.status === 'pending' || upload.status === 'uploading';
            const isFailed = upload.status === 'failed';
            const isCancelled = upload.status === 'cancelled';
            const statusLabel = isFailed
              ? 'Failed'
              : isCancelled
                ? 'Cancelled'
                : upload.status === 'completed'
                  ? 'Done'
                  : 'Uploading';

            return (
              <span
                key={upload.id}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                  isFailed
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    : isCancelled
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    : isUploading
                      ? 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400'
                      : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                }`}
              >
                {isFailed ? (
                  <AlertCircle className="w-3.5 h-3.5" />
                ) : isCancelled ? (
                  <XCircle className="w-3.5 h-3.5" />
                ) : isUploading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CheckCircle className="w-3.5 h-3.5" />
                )}
                <span className="max-w-[140px] truncate">{upload.filename}</span>
                <span className="text-[10px] uppercase tracking-wide opacity-70">
                  {statusLabel}
                </span>
              </span>
            );
          })}
          {uploads.length > uploadsToShow.length && (
            <span className="text-xs text-gray-400">
              +{uploads.length - uploadsToShow.length} more
            </span>
          )}
        </div>
      )}

      <div className="relative flex items-end gap-2 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 shadow-lg focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:focus-within:ring-sky-900/40 transition-all duration-200">
        {/* Decorative sparkle indicator when ready */}
        {status === 'ready' && value.length === 0 && (
          <div className="absolute -top-2 -left-2 p-1 rounded-full bg-amber-100 animate-pulse">
            <Sparkles className="w-3 h-3 text-amber-500" />
          </div>
        )}

        {/* Attachment button */}
        {fileUploadEnabled && (
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={isDisabled}
            className={`flex-shrink-0 p-2.5 rounded-xl transition-all duration-200 ${
              isDisabled
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-500 dark:text-gray-400 hover:text-sky-700 dark:hover:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20'
            }`}
            title="Attach files"
            data-testid="attach-files-button"
          >
            <Paperclip className="w-5 h-5" />
          </button>
        )}

        {/* Voice mode button — spec 052 Step 9: rendered as soon as a
            user is signed in, even before the first text turn. When no
            conversation is loaded yet, the button calls back into
            ``onCreateSession`` to mint one before opening the WS. */}
        {userId && (
          <VoiceButton
            sessionId={sessionId ?? ''}
            userId={userId}
            disabled={isDisabled}
            onCreateSession={onCreateSession}
          />
        )}

        {/* Textarea wrapper for autocomplete positioning */}
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isDisabled}
            rows={1}
            className={`w-full resize-none bg-transparent px-3 py-2.5 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
              focus:outline-none text-[15px] leading-relaxed
              ${isDisabled ? 'text-gray-500 dark:text-gray-500' : ''}
            `}
            style={{ minHeight: '44px', maxHeight: '120px' }}
            data-testid="chat-input"
          />

          {/* File autocomplete dropdown */}
          {autocompleteOpen && workspaceFiles.length > 0 && (
            <FileAutocomplete
              files={filteredFiles}
              query={autocompleteQuery}
              selectedIndex={autocompleteIndex}
              onSelect={handleFileSelect}
              onClose={closeAutocomplete}
            />
          )}

          {slashOpen && filteredCommands.length > 0 && (
            <div className="absolute bottom-full mb-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700">
                <Command className="w-3.5 h-3.5 text-sky-500" />
                Shortcuts
              </div>
              <div className="max-h-56 overflow-y-auto">
                {filteredCommands.map((command, index) => {
                  const isSelected = index === slashIndex;
                  return (
                    <button
                      key={command.value}
                      type="button"
                      onClick={() => handleSlashSelect(command)}
                      className={`w-full text-left px-3 py-2 flex items-start gap-2 transition-colors ${
                        isSelected ? 'bg-sky-50 dark:bg-sky-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <FileText className="w-4 h-4 text-sky-500 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                          {command.value}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {command.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {isStreaming ? (
          <button
            type="button"
            onClick={handleStopClick}
            className="flex-shrink-0 p-2.5 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white hover:from-red-600 hover:to-rose-600
              transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
            title="Stop generation"
          >
            <Square className="w-5 h-5" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!value.trim() || isDisabled || isOverLimit}
            className={`flex-shrink-0 p-2.5 rounded-xl transition-all duration-200 ${
              !value.trim() || isDisabled || isOverLimit
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'bg-gradient-to-r from-sky-600 to-sky-500 text-white hover:from-sky-700 hover:to-sky-600 shadow-md hover:shadow-lg active:scale-95'
            }`}
            title="Send message"
            data-testid="send-button"
          >
            {isSubmitted ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        )}
      </div>

      {/* Character counter — only visible when approaching the limit */}
      {isNearLimit && (
        <div className="flex items-center justify-end mt-1.5 px-1">
          <span className={`text-xs font-medium tabular-nums ${characterCountColor}`}>
            {characterCount.toLocaleString()} / {MAX_CHARACTERS.toLocaleString()}
          </span>
        </div>
      )}
    </form>
  );
});
